import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '@/store/auth';
import type { JSX, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExportActions, KpiCard } from '@evi/ui';
import {
  fetchLocationUnitsConsumed,
  locationUnitsCsvPath,
  type LocationUnitsRow,
  type LocationUnitsSort,
} from '@/lib/api';
import { downloadAuthenticatedCsv } from '@/lib/downloadAuthenticatedCsv';
import { downloadErrorMessage } from '@/lib/downloadAuthed';
import { ResponsiveTable } from '@/components/ui/ResponsiveTable';
import { formatRelativeTime } from '@/pages/dashboard/widgets/_lib';
import {
  columnsForPersona,
  type PersonaColumn,
} from '@/lib/personaColumns';
import styles from './BuildingAdminReportsPage.module.css';

interface LocationUnitsConsumedTabProps {
  /** Period chip value from the shell. Post-reports-custom-range
   *  all five chips are supported; resolved range comes through
   *  `from`/`to`. */
  period: string;
  locationIds?: readonly string[];
  /** Resolved range start (ISO) from the shell. */
  from: string;
  /** Resolved range end (ISO) from the shell. */
  to: string;
}

const PAGE_SIZE = 25;

const SORT_OPTIONS: ReadonlyArray<{ id: LocationUnitsSort; label: string }> = [
  { id: 'total_kwh_desc',    label: 'Total kWh (high → low)' },
  { id: 'sessions_desc',     label: 'Sessions (high → low)' },
  { id: 'last_session_desc', label: 'Last session (recent first)' },
  { id: 'name_asc',          label: 'Location name (A → Z)' },
];

// All building admins see all columns here (no persona-specific
// gating on Location Units — the report is operationally uniform
// for the personas that can reach it). PersonaColumn used anyway
// for consistency with BookingHistoryTab + so super_admin getting
// pulled into this tab in a future commit doesn't require a
// rewrite.
const LOCATION_UNITS_COLUMNS: ReadonlyArray<PersonaColumn<LocationUnitsRow>> = [
  {
    key: 'location_name',
    header: 'Location',
    mobile: 'primary',
    cell: (r) => r.location_name,
  },
  {
    key: 'address_short',
    header: 'Address',
    mobile: 'secondary',
    cell: (r) => r.address_short ?? <Dim>—</Dim>,
  },
  {
    key: 'session_count',
    header: 'Sessions',
    align: 'right',
    cell: (r) => r.session_count.toLocaleString(),
  },
  {
    key: 'total_kwh',
    header: 'Total kWh',
    align: 'right',
    cell: (r) => r.total_kwh.toFixed(2),
  },
  {
    key: 'avg_kwh',
    header: 'Avg kWh/Session',
    align: 'right',
    cell: (r) => r.avg_kwh.toFixed(2),
  },
  {
    key: 'unique_drivers',
    header: 'Unique Drivers',
    align: 'right',
    cell: (r) => r.unique_drivers.toLocaleString(),
  },
  {
    key: 'last_session_at',
    header: 'Last Session',
    cell: (r) => r.last_session_at
      ? <span className={styles.dimText}>{formatRelativeTime(r.last_session_at)}</span>
      : <Dim>—</Dim>,
  },
];

export function LocationUnitsConsumedTab({
  period, from, to, locationIds,
}: LocationUnitsConsumedTabProps): JSX.Element {
  const userId = useAuthStore((state) => state.user?._id);
  const [sort, setSort] = useState<LocationUnitsSort>('total_kwh_desc');
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [period, from, to, locationIds, sort]);

  const dataQ = useQuery({
    queryKey: ['building-admin-reports', 'location-units', userId, from, to, locationIds, sort, page],
    queryFn: () => fetchLocationUnitsConsumed({
      location_ids: locationIds,
      from,
      to,
      sort,
      page,
      limit: PAGE_SIZE,
    }),

    staleTime: 30_000,
  });

  // A failed export says why (29 Oct 2026), instead of failing silently.
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const handleDownloadCsv = useCallback(async () => {
    if (csvBusy) return;
    setCsvBusy(true);
    setCsvError(null);
    try {
      const csvSpec = locationUnitsCsvPath({
        location_ids: locationIds,
        from,
        to,
        sort,
      });
      const today = new Date().toISOString().slice(0, 10);
      await downloadAuthenticatedCsv({
        path: csvSpec.path,
        query: csvSpec.query,
        fallbackFilename: `location-units-consumed-${today}.csv`,
      });
    } catch (err) {
      setCsvError(await downloadErrorMessage(err));
    } finally {
      setCsvBusy(false);
    }
  }, [csvBusy, from, to, locationIds, sort]);

  // No persona gating on this tab's columns today — all building
  // admins see the same shape. PersonaColumn passthrough kept for
  // structural consistency with BookingHistoryTab.
  const columns = useMemo(() => columnsForPersona(LOCATION_UNITS_COLUMNS, null /* any */), []);

  // Post-reports-custom-range: no period-gating notice. See
  // BookingHistoryTab for the rationale.

  const kpis = dataQ.data?.kpis;
  const rows = dataQ.data?.rows ?? [];
  const total = dataQ.data?.total ?? 0;
  const totalPages = dataQ.data?.total_pages ?? 1;

  if (dataQ.isError) return <div className={styles.tabBody} role="alert"><p>Could not load this report. This is a request failure, not an empty report.</p><button className={styles.pagerBtn} onClick={() => void dataQ.refetch()}>Retry report</button></div>;

  return (
    <div className={styles.tabBody}>
      <div className={styles.kpiGrid}>
        <KpiCard
          label="Total kWh delivered"
          value={(kpis?.total_kwh ?? 0).toFixed(2)}
          unit="kWh"
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Total sessions"
          value={(kpis?.total_sessions ?? 0).toLocaleString()}
          meta={describePeriod(period)}
        />
        <KpiCard
          label="Avg kWh / session"
          value={(kpis?.avg_kwh_per_session ?? 0).toFixed(2)}
          unit="kWh"
        />
        <KpiCard
          label="Active locations"
          value={(kpis?.active_locations ?? 0).toLocaleString()}
          meta="with ≥ 1 session"
        />
      </div>

      <div className={styles.toolbar}>
        <label className={styles.sortLabel}>
          <span>Sort by</span>
          <select
            className={styles.sortSelect}
            value={sort}
            onChange={(e) => setSort(e.target.value as LocationUnitsSort)}
            aria-label="Sort"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </label>
        <div className={styles.toolbarRight}>
          <span className={styles.toolbarMeta}>
            {total > 0 ? `${total.toLocaleString()} locations` : ''}
          </span>
          <ExportActions onCsv={handleDownloadCsv} csvBusy={csvBusy} pdfHref={null} />
        </div>
      </div>
      {csvError && <p className={styles.downloadError} role="alert">{csvError}</p>}

      <div className={styles.tableCard}>
        <ResponsiveTable
          columns={columns}
          rows={rows}
          rowKey={(r) => r.location_id}
          emptyMessage={dataQ.isLoading ? 'Loading…' : 'No sessions in this date range. Try a longer period.'}
        />
      </div>

      {totalPages > 1 && (
        <div className={styles.pager}>
          <span className={styles.pagerMeta}>Page {page} of {totalPages}</span>
          <div className={styles.pagerButtons}>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
            >Previous</button>
            <button
              type="button"
              className={styles.pagerBtn}
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
            >Next</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Dim({ children }: { children: ReactNode }): JSX.Element {
  return <span className={styles.dimText}>{children}</span>;
}

// periodToFromTo removed — shell-level resolveRange owns range
// math post-reports-custom-range; from/to come in as props.

function describePeriod(period: string): string {
  if (period === '24h') return 'last 24 hours';
  if (period === '7d') return 'last 7 days';
  if (period === '30d') return 'last 30 days';
  if (period === '90d') return 'last 90 days';
  if (period === 'custom') return 'custom range';
  return '';
}
